import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { api, streamSse } from '@/lib/api';
import {
  Sparkles,
  Clapperboard,
  Layers,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
} from 'lucide-react';

const ATLAS_TABS: { key: string; label: string }[] = [
  { key: 'world', label: '世界观' },
  { key: 'characters', label: '人物' },
  { key: 'npc', label: 'NPC' },
  { key: 'outline', label: '大纲' },
];

const AGENT_STATUS_POLL_INTERVAL_MS = 6000;
const AGENT_STATUS_MAX_STALE_POLLS = 100;

type Phase = 'idle' | 'loading' | 'scripts' | 'atlas' | 'done' | 'error';

interface EpisodeStatus {
  index: number;
  title?: string;
  summary?: string;
  charCount?: number;
  hasTitle?: boolean;
  done: boolean;
  skipped?: boolean;
  failed?: boolean;
}

type AgentEvent =
  | { event: 'run_started'; runId?: string }
  | { event: 'phase'; phase: 'loading' | 'scripts' | 'atlas'; totalEpisodes?: number; totalTabs?: number; completedEpisodes?: number }
  | { event: 'episode_titles_batch'; titles: { index: number; title: string; summary: string }[]; totalEpisodes: number }
  | { event: 'episode_start'; episodeIndex: number; of: number; runId?: string }
  | { event: 'episode_done'; episodeIndex: number; of?: number; title?: string; sceneCount?: number; charCount?: number; skipped?: boolean; failed?: boolean; runId?: string }
  | { event: 'atlas_tab_done'; tab: string; filled: boolean; fallback?: boolean }
  | { event: 'done'; totalEpisodes: number; atlasTabs: string[] }
  | { event: 'busy'; message: string }
  | { event: 'cancelled'; reason: string }
  | { event: 'error'; message: string; episodeIndex?: number };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  episodeId: string;
  onFinish: () => void;
  onSwitchToRefine: () => void;
  /** 来自 SsmlDoc.meta.episodes 的初始状态,用于在 SSE 接通前立即渲染真实进度. */
  initialEpisodes?: Array<{
    index: number;
    title?: string;
    summary?: string;
    sceneCount?: number;
    charCount?: number;
    hasScenes?: boolean;
  }>;
  /** SsmlDoc.meta.episodeCount,优先于 SSE 中的 totalEpisodes 显示. */
  initialTotalEpisodes?: number;
}

export function AgentWorkflowDialog({
  open,
  onOpenChange,
  episodeId,
  onFinish,
  onSwitchToRefine,
  initialEpisodes,
  initialTotalEpisodes,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [totalEpisodes, setTotalEpisodes] = useState(initialTotalEpisodes ?? 0);
  const [episodes, setEpisodes] = useState<Record<number, EpisodeStatus>>({});
  const [currentEps, setCurrentEps] = useState<Set<number>>(new Set());
  const [atlasDone, setAtlasDone] = useState<Record<string, boolean>>({});
  const [agentRunning, setAgentRunning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorEp, setErrorEp] = useState<number | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);
  const startedRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentRunIdRef = useRef<string | null>(null);
  const pollCountRef = useRef(0);
  const episodesRef = useRef<Record<number, EpisodeStatus>>({});

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const getCompletedCount = (items: Record<number, EpisodeStatus>) =>
    Object.values(items).filter((e) => e.done).length;

  useEffect(() => {
    episodesRef.current = episodes;
  }, [episodes]);

  /** GET 后端最新真实快照,完整覆盖本地 state.前端打开 Modal 时调用. */
  const fetchSnapshot = async (): Promise<{ status: 'done' | 'running' | 'pending' | 'error'; hasProgress: boolean; hasStarted: boolean }> => {
    try {
      const snap = await api
        .get(`writer/episodes/${episodeId}/agent/status`)
        .json<{
          phase: 'idle' | 'scripts' | 'atlas' | 'done';
          running?: boolean;
          totalEpisodes: number;
          completedEpisodes: number;
          titledEpisodes: number;
          episodes: Array<{
            index: number;
            title: string;
            summary: string;
            charCount: number;
            sceneCount: number;
            hasScenes: boolean;
            failed?: boolean;
          }>;
          atlasFilled: string[];
          runningEpisodes?: number[];
          hasStarted?: boolean;
          lastError?: string | null;
          lastFailedAt?: string | null;
          missingEpisodes?: number[];
        }>();
      setTotalEpisodes(snap.totalEpisodes);
      const seeded: Record<number, EpisodeStatus> = {};
      for (const ep of snap.episodes) {
        seeded[ep.index] = {
          index: ep.index,
          title: ep.title,
          summary: ep.summary,
          charCount: ep.charCount,
          hasTitle: Boolean(ep.title),
          done: ep.hasScenes,
          failed: ep.failed,
        };
      }
      const missingSet = new Set(snap.missingEpisodes ?? []);
      for (const idx of missingSet) {
        seeded[idx] = {
          ...(seeded[idx] ?? { index: idx, done: false }),
          index: idx,
          done: seeded[idx]?.done ?? false,
          failed: true,
        };
      }
      setEpisodes(seeded);
      setAgentRunning(Boolean(snap.running));
      const runningSet = new Set(snap.runningEpisodes ?? []);
      setCurrentEps(runningSet);
      const atlasMap: Record<string, boolean> = {};
      for (const tab of snap.atlasFilled) atlasMap[tab] = true;
      setAtlasDone(atlasMap);
      const hasProgress = snap.completedEpisodes > 0;
      const hasStarted = Boolean(snap.hasStarted);
      if (snap.phase === 'done') {
        setPhase('done');
        setErrorMsg(null);
        setErrorEp(null);
        return { status: 'done', hasProgress, hasStarted };
      }
      if (!snap.running && snap.lastError) {
        setPhase('error');
        setErrorMsg(snap.lastError);
        const [firstMissing] = snap.missingEpisodes ?? [];
        setErrorEp(typeof firstMissing === 'number' ? firstMissing : null);
        return { status: 'error', hasProgress, hasStarted };
      }
      setPhase(snap.phase === 'idle' ? 'loading' : snap.phase);
      if (snap.running) {
        setErrorMsg(null);
        setErrorEp(null);
      }
      return { status: snap.running ? 'running' : 'pending', hasProgress, hasStarted };
    } catch (err) {
      setPhase('error');
      setErrorMsg(err instanceof Error ? err.message : String(err));
      return { status: 'error', hasProgress: false, hasStarted: true };
    }
  };

  /** 后端单 flight 已在跑时,周期性 poll status 同步进度.直到 done 或 Dialog 关闭. */
  const startPolling = () => {
    stopPolling();
    pollCountRef.current = 0;
    let lastCompleted = -1;
    const tick = async () => {
      const result = await fetchSnapshot();
      if (result.status === 'done' || result.status === 'error') return;
      const currentCompleted = getCompletedCount(episodesRef.current);
      if (currentCompleted > lastCompleted) {
        lastCompleted = currentCompleted;
        pollCountRef.current = 0;
      } else {
        pollCountRef.current += 1;
      }
      if (result.status === 'running' && pollCountRef.current >= AGENT_STATUS_MAX_STALE_POLLS) {
        setAgentRunning(false);
        return;
      }
      pollTimerRef.current = setTimeout(tick, AGENT_STATUS_POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(tick, AGENT_STATUS_POLL_INTERVAL_MS);
  };

  /** 仅用作 GET 之前的瞬时占位,避免空闪.不再清 episodes/atlasDone. */
  const hydrateFromProps = () => {
    if (!initialEpisodes || initialEpisodes.length === 0) return;
    const seeded: Record<number, EpisodeStatus> = {};
    for (const ep of initialEpisodes) {
      if (typeof ep.index !== 'number' || ep.index < 1) continue;
      const hasScenes = Boolean(ep.hasScenes ?? (ep.sceneCount ?? 0) > 0);
      seeded[ep.index] = {
        index: ep.index,
        title: ep.title,
        summary: ep.summary,
        charCount: ep.charCount ?? 0,
        hasTitle: Boolean(ep.title),
        done: hasScenes,
      };
    }
    setEpisodes(seeded);
    if (initialTotalEpisodes && initialTotalEpisodes > 0) {
      setTotalEpisodes(initialTotalEpisodes);
    }
  };

  const start = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    stopPolling();
    setPhase('loading');
    setErrorMsg(null);
    setErrorEp(null);

    const ctrl = streamSse<AgentEvent>(
      `writer/episodes/${episodeId}/agent/run`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
      (ev) => {
        if (ev.event === 'run_started') {
          currentRunIdRef.current = ev.runId ?? null;
          setCurrentEps(new Set());
          setAgentRunning(true);
          // SSE 在 probe 阶段可能暂不吐 episode_start;用 status 轮询兜底同步 runningEpisodes。
          startPolling();
        } else if (ev.event === 'phase') {
          setPhase(ev.phase);
          if (typeof ev.totalEpisodes === 'number') setTotalEpisodes(ev.totalEpisodes);
        } else if (ev.event === 'episode_titles_batch') {
          setTotalEpisodes(ev.totalEpisodes);
          setEpisodes((m) => {
            const next = { ...m };
            for (const t of ev.titles) {
              const prev = next[t.index];
              next[t.index] = {
                index: t.index,
                title: t.title,
                summary: t.summary,
                charCount: prev?.charCount ?? 0,
                hasTitle: true,
                done: prev?.done ?? false,
                skipped: prev?.skipped,
              };
            }
            return next;
          });
        } else if (ev.event === 'episode_start') {
          if (ev.runId && currentRunIdRef.current && ev.runId !== currentRunIdRef.current) return;
          setTotalEpisodes(ev.of);
          setCurrentEps((s) => {
            const next = new Set(s);
            next.add(ev.episodeIndex);
            return next;
          });
          // 该集开始(重新)生成,清掉历史失败红色标记
          setEpisodes((m) => {
            const prev = m[ev.episodeIndex];
            if (!prev || !prev.failed) return m;
            return { ...m, [ev.episodeIndex]: { ...prev, failed: false } };
          });
        } else if (ev.event === 'episode_done') {
          if (ev.runId && currentRunIdRef.current && ev.runId !== currentRunIdRef.current) return;
          setCurrentEps((s) => {
            if (!s.has(ev.episodeIndex)) return s;
            const next = new Set(s);
            next.delete(ev.episodeIndex);
            return next;
          });
          setEpisodes((m) => ({
            ...m,
            [ev.episodeIndex]: {
              index: ev.episodeIndex,
              title: ev.title ?? m[ev.episodeIndex]?.title,
              summary: m[ev.episodeIndex]?.summary,
              charCount: ev.charCount ?? m[ev.episodeIndex]?.charCount ?? 0,
              hasTitle: m[ev.episodeIndex]?.hasTitle ?? Boolean(ev.title),
              done: !ev.failed,
              skipped: ev.skipped,
              failed: ev.failed,
            },
          }));
        } else if (ev.event === 'atlas_tab_done') {
          setAtlasDone((m) => ({ ...m, [ev.tab]: ev.filled }));
        } else if (ev.event === 'done') {
          setPhase('done');
          setAgentRunning(false);
          ctrlRef.current = null;
          stopPolling();
          onFinish();
        } else if (ev.event === 'busy') {
          ctrlRef.current?.abort();
          ctrlRef.current = null;
          startedRef.current = false;
          startPolling();
        } else if (ev.event === 'cancelled') {
          setAgentRunning(false);
          ctrlRef.current?.abort();
          ctrlRef.current = null;
          stopPolling();
          onOpenChange(false);
        } else if (ev.event === 'error') {
          setPhase('error');
          setAgentRunning(false);
          setErrorMsg(ev.message);
          if (typeof ev.episodeIndex === 'number') setErrorEp(ev.episodeIndex);
          ctrlRef.current = null;
          stopPolling();
        }
      },
      (err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setPhase('error');
        setAgentRunning(false);
        setErrorMsg(err instanceof Error ? err.message : String(err));
        ctrlRef.current = null;
        stopPolling();
      },
    );
    ctrlRef.current = ctrl;
  };

  useEffect(() => {
    if (open) {
      // 1) 用 props 做瞬时占位,避免空闪
      hydrateFromProps();
      // 2) GET 后端真实快照,完整覆盖
      fetchSnapshot().then((result) => {
        if (result.status === 'done') {
          return;
        }
        if (result.status === 'running') {
          startPolling();
          return;
        }
        // 只在真正从未启动过的项目上自动开始。中断/取消/失败后交给用户手动重试,
        // 避免打开弹窗就反复触发新的 /agent/run。
        if (result.status === 'pending' && !result.hasStarted && !startedRef.current) {
          setTimeout(start, 30);
        }
        // pending + has progress = previous run interrupted, show state and let user click retry
      });
    } else {
      // 关闭只中止流 + 停轮询,不清 episodes/atlasDone — 下次打开前 GET 会刷新
      ctrlRef.current?.abort();
      ctrlRef.current = null;
      startedRef.current = false;
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const retry = () => {
    startedRef.current = false;
    setPhase('idle');
    setErrorMsg(null);
    setErrorEp(null);
    // 重新生成会重试所有未完成集,立即清掉历史红色失败标记,避免用户看到永久假失败
    setEpisodes((m) => {
      const next: Record<number, EpisodeStatus> = {};
      for (const [k, v] of Object.entries(m)) {
        next[Number(k)] = v.done ? v : { ...v, failed: false };
      }
      return next;
    });
    setTimeout(start, 30);
  };

  const handleClose = () => {
    ctrlRef.current?.abort();
    ctrlRef.current = null;
    onOpenChange(false);
  };

  const completedEpisodes = Object.values(episodes).filter((e) => e.done).length;
  const titledEpisodes = Object.values(episodes).filter((e) => e.hasTitle).length;
  const atlasFilled = Object.values(atlasDone).filter(Boolean).length;
  const scriptProgress = totalEpisodes > 0 ? Math.round((completedEpisodes / totalEpisodes) * 100) : 0;
  const atlasProgress = Math.round((atlasFilled / ATLAS_TABS.length) * 100);
  const currentEpsList = Array.from(currentEps).sort((a, b) => a - b);

  const isRunning = agentRunning && (phase === 'loading' || phase === 'scripts' || phase === 'atlas');
  const hasStalled = !agentRunning && (
    (phase === 'scripts' && totalEpisodes > 0 && completedEpisodes < totalEpisodes)
    || (phase === 'atlas' && atlasFilled < ATLAS_TABS.length)
  );
  const hasFailed = Object.values(episodes).some((e) => e.failed);
  const showRetry = phase === 'error' || hasStalled || hasFailed;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
        else onOpenChange(true);
      }}
      title="编剧 Agent 工作流"
      description="自动生成每集脚本 → 回填 Story Atlas 8 个 tab,过程中可继续浏览其他页面"
      className="max-w-2xl"
      footer={
        <div className="ml-auto flex gap-2">
          {showRetry && !isRunning && (
            <Button variant="secondary" size="sm" onClick={retry}>
              <RotateCcw className="h-4 w-4" />
              {hasFailed || hasStalled ? `重试失败集 (${totalEpisodes - completedEpisodes} 集)` : '重试(已生成集不重做)'}
            </Button>
          )}
          {phase === 'done' ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                关闭
              </Button>
              <Button
                variant="agent"
                agentColor="writer"
                size="sm"
                onClick={() => {
                  onSwitchToRefine();
                  onOpenChange(false);
                }}
              >
                去脚本微调 <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={phase === 'loading'}
            >
              {isRunning ? '隐藏(后台继续)' : '关闭'}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-5 py-3 text-sm">
        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          <StepChip
            icon={<Clapperboard className="h-3.5 w-3.5" />}
            label={`脚本生成 ${completedEpisodes}/${totalEpisodes || '?'}`}
            active={phase === 'scripts' || phase === 'loading'}
            done={phase === 'atlas' || phase === 'done'}
          />
          <span className="text-outline">→</span>
          <StepChip
            icon={<Layers className="h-3.5 w-3.5" />}
            label={`Atlas 回填 ${atlasFilled}/${ATLAS_TABS.length}`}
            active={phase === 'atlas'}
            done={phase === 'done'}
          />
          <span className="text-outline">→</span>
          <StepChip
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label="完成"
            active={false}
            done={phase === 'done'}
          />
        </div>

        {/* 错误 */}
        {phase === 'error' && errorMsg && (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft/30 p-3 text-danger">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1 space-y-1">
              <strong className="text-sm">Agent 中断</strong>
              <p className="text-xs leading-relaxed">{errorMsg}</p>
              {errorEp !== null && (
                <p className="text-[11px] opacity-80">已生成 {completedEpisodes} 集 · 卡在第 {errorEp} 集 · 重试将从断点继续</p>
              )}
            </div>
          </div>
        )}

        {/* Phase A · 脚本 */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold">
              <Clapperboard className="h-4 w-4 text-brand-600" />
              分集脚本
            </h3>
            <span className="text-xs tabular-nums text-neutral-500">
              {totalEpisodes > 0 ? `${completedEpisodes} / ${totalEpisodes} 集` : '准备中…'}
            </span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-variant">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600 shadow-[0_0_8px_rgba(99,102,241,0.4)] transition-[width] duration-500 ease-out"
              style={{ width: `${scriptProgress}%` }}
            />
            {scriptProgress > 0 && scriptProgress < 100 && isRunning && (
              <div className="pointer-events-none absolute inset-0 -translate-x-full animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            )}
          </div>
          {phase === 'loading' && (
            <div className="flex items-center gap-2 rounded-md bg-brand-50/60 px-2.5 py-1.5 text-xs text-brand-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
              </span>
              正在分批生成 {Math.max(0, totalEpisodes - titledEpisodes) || totalEpisodes} 集标题与概览(每批 3 集),稍候…
            </div>
          )}
          {phase === 'scripts' && currentEpsList.length > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-brand-50/60 px-2.5 py-1.5 text-xs text-brand-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-500" />
              </span>
              并发生成第 {currentEpsList.join(', ')} 集场景…
            </div>
          )}
          {phase === 'scripts' && currentEpsList.length === 0 && titledEpisodes > 0 && completedEpisodes < totalEpisodes && (
            <div className="rounded-md bg-brand-50/60 px-2.5 py-1.5 text-xs text-brand-700">
              已批量生成 {titledEpisodes} 集标题与概览,正在并发补充每集场景…
            </div>
          )}
          {totalEpisodes > 0 && (
            <div className="grid max-h-44 grid-cols-8 gap-1 overflow-y-auto rounded-lg bg-surface-variant/30 p-2 sm:grid-cols-12">
              {Array.from({ length: totalEpisodes }, (_, i) => i + 1).map((idx) => {
                const ep = episodes[idx];
                const active = currentEps.has(idx);
                const done = ep?.done;
                const titled = ep?.hasTitle;
                const failed = ep?.failed;
                const pollingPending = isRunning && currentEps.size === 0 && !done && !failed;
                return (
                  <div
                    key={idx}
                    title={ep?.title ?? `第 ${idx} 集`}
                    className={
                      failed
                        ? 'flex h-6 items-center justify-center rounded bg-danger/70 text-[10px] font-medium text-white'
                        : done
                        ? 'flex h-6 items-center justify-center rounded bg-brand-500 text-[10px] font-medium text-white'
                        : active
                        ? 'flex h-6 items-center justify-center rounded bg-brand-200 text-[10px] font-medium text-brand-700 ring-2 ring-brand-500'
                        : pollingPending
                        ? 'flex h-6 items-center justify-center rounded bg-brand-100/60 text-[10px] font-medium text-brand-500'
                        : titled
                        ? 'flex h-6 items-center justify-center rounded bg-brand-100/80 text-[10px] font-medium text-brand-700'
                        : 'flex h-6 items-center justify-center rounded bg-surface text-[10px] text-neutral-400'
                    }
                  >
                    {idx}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Phase B · Atlas */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold">
              <Layers className="h-4 w-4 text-brand-600" />
              Story Atlas 回填
            </h3>
            <span className="text-xs tabular-nums text-neutral-500">
              {atlasFilled} / {ATLAS_TABS.length} tab
            </span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-variant">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-600 shadow-[0_0_8px_rgba(99,102,241,0.4)] transition-[width] duration-500 ease-out"
              style={{ width: `${atlasProgress}%` }}
            />
            {atlasProgress > 0 && atlasProgress < 100 && isRunning && (
              <div className="pointer-events-none absolute inset-0 -translate-x-full animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            {ATLAS_TABS.map((t) => {
              const filled = atlasDone[t.key];
              const active = phase === 'atlas' && filled === undefined;
              return (
                <div
                  key={t.key}
                  className={
                    filled
                      ? 'flex items-center gap-1.5 rounded-md border border-brand-500/40 bg-brand-50 px-2 py-1 text-xs text-brand-700'
                      : active
                      ? 'flex items-center gap-1.5 rounded-md border border-outline-variant/40 bg-surface px-2 py-1 text-xs text-neutral-500'
                      : 'flex items-center gap-1.5 rounded-md border border-dashed border-outline-variant/30 bg-surface-variant/30 px-2 py-1 text-xs text-neutral-400'
                  }
                >
                  {filled ? <CheckCircle2 className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                  {t.label}
                </div>
              );
            })}
          </div>
        </section>

        {phase === 'done' && (
          <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success-soft/30 p-3 text-success">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1 text-sm">
              <strong>Agent 工作流完成</strong>
              <p className="mt-0.5 text-xs leading-relaxed text-success/80">
                已生成 {completedEpisodes} 集脚本 · Atlas {atlasFilled}/{ATLAS_TABS.length} tab 已回填,可去「脚本微调」继续逐集润色
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function StepChip({
  icon,
  label,
  active,
  done,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <span
      className={
        done
          ? 'inline-flex items-center gap-1 rounded-full bg-success-soft/60 px-2.5 py-1 text-success'
          : active
          ? 'inline-flex items-center gap-1 rounded-full bg-brand-100/80 px-2.5 py-1 text-brand-700 ring-1 ring-brand-300'
          : 'inline-flex items-center gap-1 rounded-full bg-surface-variant/50 px-2.5 py-1 text-neutral-500'
      }
    >
      {icon}
      <span className="font-medium">{label}</span>
    </span>
  );
}
